import { h, p as proxyCustomElement, H, F as Fragment, a as Host } from './index.js';
import { d as defineCustomElement$5 } from './p-BedvZv52.js';
import { d as defineCustomElement$4 } from './p-aAvJ0uEW.js';
import { d as defineCustomElement$3 } from './p-DaiIyqo3.js';
import { d as defineCustomElement$2 } from './p-Bjt5CD4D.js';
import { d as defineCustomElement$1 } from './p-QtR5VUMp.js';

// biome-ignore lint/correctness/noUnusedImports: needed for Stencil
const CopyToClipboardButton = ({ code }) => {
    const copyToClipboard = async () => {
        if (!code) {
            return;
        }
        try {
            await navigator.clipboard.writeText(code);
        }
        catch {
            console.warn('Copy to clipboard not supported');
        }
    };
    return (h("wui-button", { onClick: () => {
            copyToClipboard();
        }, title: "Copy", type: "icon", icon: "copy", tooltip: {
            label: 'Copied!',
            trigger: 'click',
        } }));
};

const codeSnippetCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;border-radius:.25rem;box-sizing:border-box;display:flex;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;max-width:100%;min-height:1.25rem;position:relative;transition:background-color .2s ease}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}:host .code{color:var(--jw-theme-code-editor-foreground)}:host .hljs-comment,:host .hljs-quote{color:var(--jw-theme-code-editor-comments);font-style:italic}:host .hljs-attr,:host .hljs-attribute,:host .hljs-deletion,:host .hljs-name,:host .hljs-regexp,:host .hljs-tag,:host .hljs-template-variable,:host .hljs-variable{color:var(--jw-theme-code-editor-variables)}:host .hljs-built_in,:host .hljs-builtin-name,:host .hljs-link,:host .hljs-literal,:host .hljs-meta,:host .hljs-number,:host .hljs-params,:host .hljs-type{color:var(--jw-theme-code-editor-numbers)}:host .hljs-addition,:host .hljs-string,:host .hljs-symbol{color:var(--jw-theme-code-editor-strings)}:host .hljs-bullet,:host .hljs-punctuation{color:var(--jw-theme-code-editor-foreground)}:host .hljs-section,:host .hljs-selector-class,:host .hljs-selector-id,:host .hljs-title.class_{color:var(--jw-theme-code-editor-classes)}:host .hljs-title.function_{color:var(--jw-theme-code-editor-functions)}:host .hljs-keyword,:host .hljs-selector-tag{color:var(--jw-theme-code-editor-keywords)}:host .hljs-operator{color:var(--jw-theme-code-editor-operators)}.content-container{overflow-y:auto}.code{font-family:Inconsolata;font-size:.9375rem;font-weight:700;letter-spacing:0;line-height:1.25rem;width:100%}:host([type=inline]){align-items:center;background-color:var(--jw-theme-form-addon-default-bg-color);display:inline-flex;font-size:inherit!important;justify-content:center;line-height:inherit!important}:host([type=inline]) .content{padding:.25rem .5rem}:host([type=inline]) .code{color:var(--jw-theme-code-editor-foreground);font-size:inherit!important;line-height:inherit!important;white-space:nowrap}:host([type=block]),:host([type=multiline]){background-color:var(--jw-theme-form-field-default-bg-color);border:1px solid var(--jw-theme-form-border-regular-default-border-color);gap:1rem;justify-content:space-between;position:relative;width:100%}:host([type=block]) .content-container,:host([type=multiline]) .content-container{display:block;max-height:100%;width:100%}:host([type=block]) .content,:host([type=multiline]) .content{padding-left:.75rem;padding-right:0}:host([type=block]) .content-container,:host([type=inline]) .content-container{overflow:hidden}:host([type=block]) .code,:host([type=inline]) .code{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}:host([type=block]){align-items:center;height:2rem;max-width:100%}:host([type=block]) .content-container{align-items:center}:host([type=block]) .code{color:var(--jw-theme-code-editor-strings)}:host([type=multiline]) .content{padding-bottom:.5rem;padding-top:.5rem}:host([type=multiline]) .code{overflow-wrap:break-word;tab-size:2;white-space:pre-wrap;word-break:break-all}:host([data-focused]){outline:var(--jw-theme-effects-focus-default-outline)}::slotted(textarea){background:none;border:none;box-sizing:inherit;color:inherit;font-family:Inconsolata;font-size:.9375rem;font-variant-ligatures:inherit;font-weight:700;height:100%;left:0;letter-spacing:0;line-height:1.25rem;margin:0;min-height:inherit;overflow:hidden;overflow-wrap:break-word;padding:0;position:absolute;resize:none;tab-size:2;text-indent:inherit;text-rendering:inherit;text-transform:inherit;top:0;white-space:pre-wrap;width:100%;word-break:break-all;-webkit-font-smoothing:antialiased;-webkit-text-fill-color:#0000}::slotted(textarea:focus){outline:none}:host([disabled]){background-color:var(--jw-theme-form-field-disabled-bg-color);cursor:not-allowed;opacity:.4;padding-right:.75rem}:host([disabled]) .code{color:var(--jw-theme-form-field-disabled-color)}.buttons-container{display:flex;flex-direction:row;gap:.25rem}";

function replaceTextWithBullets(code) {
    if (!code) {
        return;
    }
    return '\u2022'.repeat(code.length);
}
const WuiCodeSnippet = /*@__PURE__*/ proxyCustomElement(class WuiCodeSnippet extends H {
    constructor(registerHost) {
        super();
        if (registerHost !== false) {
            this.__registerHost();
        }
        this.__attachShadow();
        /**
         * Display block or inline style
         */
        this.type = 'block';
        this.disabled = false;
        /**
         * Code to be rendered
         */
        this.code = '';
        /**
         * Manually toggle syntax highlighting
         *
         * Only applies to multiline, defaults to true
         */
        this.highlight = undefined;
        /**
         * When true, replaces code with bullets until hover/focus
         */
        this.secret = false;
        /**
         * Syntax highlighting language.
         *
         * Recommended to set this manually instead of doing automatic assertion.
         */
        this.language = 'plaintext';
        /**
         * Prepend extra buttons to the menu
         */
        this.buttons = ['copy-to-clipboard'];
        this._codeChanged = false;
        this.focused = false;
    }
    _code() {
        // Keep switching on/off when the code prop changes
        this._codeChanged = !this._codeChanged;
    }
    renderCode() {
        const showCode = this.secret ? this.focused : true;
        const code = showCode ? this.code : replaceTextWithBullets(this.code);
        // No need to add a long string of code to the title
        const title = showCode && this.type !== 'multiline' ? this.code : '';
        // Don't truncate bullets, clip them instead
        const style = showCode ? {} : { textOverflow: 'clip' };
        // If highlight is undefined, default to true when type is multiline
        const highlight = typeof this.highlight === 'undefined' ? this.type === 'multiline' : this.highlight;
        if (highlight) {
            // Use _codeChanged as key to force creating a new element when code changes
            // Each time the code prop gets updated, the flag toggles, allowing us
            // to recreate the div
            return (h("div", { key: this._codeChanged ? 1 : 0, class: `code ${this.language || ''}`, title: title, style: style, ref: async (el) => {
                    if (!el) {
                        return;
                    }
                    const hljs = (await import('./p-Cs6R4ui0.js').then(function (n) { return n.i; })).default;
                    if (el.dataset.highlighted) {
                        delete el.dataset.highlighted;
                    }
                    hljs.highlightElement(el);
                } }, code));
        }
        return (h("div", { class: "code", title: title, style: style }, code));
    }
    render() {
        return (h(Host, { key: 'b9c42d547f1128436a509db41254d5d528e9f497', "data-focused": this.focused }, h("div", { key: '9112306974c33c2eaa9ebe181a5f74467f274cc1', class: "content-container", onFocus: this.secret
                ? () => {
                    this.focused = true;
                }
                : undefined, onBlur: this.secret
                ? () => {
                    this.focused = false;
                }
                : undefined, tabIndex: this.secret && this.type === 'block' ? 0 : undefined }, h("div", { key: 'e43bc4187888f2664220e27d85daaf72f8ba4284', part: "content", class: "content" }, this.loading ? (h("wui-loader", null)) : (h(Fragment, null, h("slot", { name: "textarea" }), this.renderCode())))), Boolean(this.type !== 'inline' && this.buttons?.length && !this.disabled) && (h("div", { key: 'e982fa6dbe863150cf6cdaa256dac50e4346b990', class: "buttons-container" }, this.buttons?.map((button) => {
            if (button === 'copy-to-clipboard') {
                return h(CopyToClipboardButton, { code: this.code });
            }
            return (h("wui-button", { key: button.title, onClick: () => button.action(this.elm), title: button.title, type: "icon", icon: button.icon }));
        })))));
    }
    get elm() { return this; }
    static get watchers() { return {
        "code": ["_code"]
    }; }
    static get style() { return codeSnippetCss; }
}, [257, "wui-code-snippet", {
        "type": [513],
        "disabled": [516],
        "code": [1],
        "highlight": [4],
        "secret": [516],
        "language": [513],
        "loading": [516],
        "buttons": [16],
        "focused": [32]
    }, undefined, {
        "code": ["_code"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-code-snippet", "wui-button", "wui-icon", "wui-loader", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-code-snippet":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiCodeSnippet);
            }
            break;
        case "wui-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-loader":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { CopyToClipboardButton as C, WuiCodeSnippet as W, defineCustomElement as d };
